import { z } from "zod";

const PAGE_SIZE = 10;

export const companySortBySchema = z.enum([
  "nameEn",
  "nameAr",
  "email",
  "admin",
  "createdAt",
]);

export const companiesSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    sortBy: companySortBySchema.optional(),
    search: z.string().trim().min(1).optional(),
    type: z.enum(["COMPANY", "INDIVIDUAL"]).optional(),
    fromCreatedAt: z.string().datetime().optional(),
    toCreatedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromCreatedAt === undefined) ===
      (search.toCreatedAt === undefined),
  );

export type CompaniesSearch = z.infer<typeof companiesSearchSchema>;
export type CompanySortBy = z.infer<typeof companySortBySchema>;

// `GET /accounts/archived` only sorts by archive date (`sort`), so there is no
// `sortBy`. `from/toArchivedAt` are set together.
export const archivedCompaniesSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().trim().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedCompaniesSearch = z.infer<
  typeof archivedCompaniesSearchSchema
>;

export const adminSortBySchema = z.enum([
  "id",
  "username",
  "nameEn",
  "nameAr",
  "accountName",
  "email",
  "status",
  "createdAt",
]);

export const accountAdminStatusSchema = z.enum([
  "ACTIVE",
  "INACTIVE",
  "PENDING_ACTIVATION",
]);

export const adminsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  sort: z.enum(["asc", "desc"]).optional(),
  sortBy: adminSortBySchema.optional(),
  search: z.string().trim().min(1).optional(),
  accountId: z.string().uuid().optional(),
  status: accountAdminStatusSchema.optional(),
});

export type AdminsSearch = z.infer<typeof adminsSearchSchema>;
export type AdminSortBy = z.infer<typeof adminSortBySchema>;

// `GET /users/accounts-admins/archived` only sorts by archive date (`sort`),
// so there is no `sortBy`. `from/toArchivedAt` are set together.
export const archivedAdminsSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().trim().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedAdminsSearch = z.infer<typeof archivedAdminsSearchSchema>;

export const regionSortBySchema = z.enum(["nameEn", "nameAr", "createdAt"]);

export const regionsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  sort: z.enum(["asc", "desc"]).optional(),
  sortBy: regionSortBySchema.optional(),
  // No .trim() -- the input's value is this field round-tripped through the
  // URL on every keystroke, so trimming here would eat spaces (including a
  // trailing one) as the user types a multi-word search.
  search: z.string().min(1).optional(),
});

export type RegionsSearch = z.infer<typeof regionsSearchSchema>;
export type RegionSortBy = z.infer<typeof regionSortBySchema>;

// The archived-regions list only sorts by archive date, same as
// archivedBranchesSearchSchema -- no sortBy. `from/toArchivedAt` are set
// together.
export const archivedRegionsSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().trim().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedRegionsSearch = z.infer<typeof archivedRegionsSearchSchema>;

export const branchSortBySchema = z.enum(["nameEn", "nameAr", "createdAt"]);

export const branchesSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  sort: z.enum(["asc", "desc"]).optional(),
  sortBy: branchSortBySchema.optional(),
  // No .trim() -- see regionsSearchSchema's comment.
  search: z.string().min(1).optional(),
});

export type BranchesSearch = z.infer<typeof branchesSearchSchema>;

// The archived-branches list only sorts by archive date, same as
// archivedCompaniesSearchSchema -- no sortBy. `from/toArchivedAt` are set
// together.
export const archivedBranchesSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().trim().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedBranchesSearch = z.infer<typeof archivedBranchesSearchSchema>;

export const departmentSortBySchema = z.enum(["nameEn", "nameAr"]);

export const departmentsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  sort: z.enum(["asc", "desc"]).optional(),
  sortBy: departmentSortBySchema.optional(),
  // No .trim() -- see regionsSearchSchema's comment.
  search: z.string().min(1).optional(),
});

export type DepartmentsSearch = z.infer<typeof departmentsSearchSchema>;
export type DepartmentSortBy = z.infer<typeof departmentSortBySchema>;

// The archive only sorts by archive date (`sort`), so there is no `sortBy`.
// `from/toArchivedAt` are set together.
export const archivedDepartmentsSearchSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
    sort: z.enum(["asc", "desc"]).optional(),
    search: z.string().trim().min(1).optional(),
    fromArchivedAt: z.string().datetime().optional(),
    toArchivedAt: z.string().datetime().optional(),
  })
  .refine(
    (search) =>
      (search.fromArchivedAt === undefined) ===
      (search.toArchivedAt === undefined),
  );

export type ArchivedDepartmentsSearch = z.infer<
  typeof archivedDepartmentsSearchSchema
>;
